import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TemplateService } from '../../data/services/view/template.service';
import { SmsEncodingService } from '../../data/services/utilities/sms-encoding.service';
import { ApiCallsService } from '../../data/services/api/api-calls.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss']
})
export class TemplateFormComponent implements OnInit, OnDestroy {
  activeTabIndex = 0; // Tab activo (0=Básico, 1=Email, 2=SMS, 3=WhatsApp, 4=Llamadas)
  activeFieldPath: string | null = null; // Campo que tiene el focus actualmente
  cursorPosition: number = 0; // Posición del cursor en el campo activo
  activeFieldElement: HTMLInputElement | HTMLTextAreaElement | null = null; // Referencia al elemento DOM activo
  
  // Propiedades para manejo de audio
  selectedAudioFile: File | null = null;
  audioPreviewUrl: string | null = null;
  isUploadingAudio = false;

  // Opciones para el selector de tipo de llamada
  callTypeOptions = [
    { label: 'Texto a Voz (TTS)', value: 'TTS', icon: 'pi pi-comment' },
    { label: 'Audio Pregrabado', value: 'AUDIO', icon: 'pi pi-volume-up' }
  ];
  
  get form() {
    return this.templateService.entity;
  }
  
  get emailTemplate() {
    return this.form.get('emailTemplate') as FormGroup;
  }
  
  get smsTemplate() {
    return this.form.get('smsTemplate') as FormGroup;
  }
  
  get whatsappTemplate() {
    return this.form.get('whatsappTemplate') as FormGroup;
  }
  
  get callScript() {
    return this.form.get('callScript') as FormGroup;
  }

  get autoCallConfig() {
    return this.form.get('autoCallConfig') as FormGroup;
  }
  
  // Indica si el tab activo permite insertar variables
  get canInsertVariables(): boolean {
    return this.activeTabIndex !== 0; // No permitir en tab "Información Básica"
  }

  constructor(
    public templateService: TemplateService,
    private smsEncodingService: SmsEncodingService,
    private apiCallsService: ApiCallsService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    // Inicializar si es necesario
  }
  
  /**
   * Actualiza la posición del cursor del campo activo
   */
  updateCursorPosition() {
    if (this.activeFieldElement) {
      this.cursorPosition = this.activeFieldElement.selectionStart || 0;
    }
  }
  
  /**
   * Establece el campo actualmente en focus para inserción de variables
   */
  setActiveField(fieldPath: string, event?: FocusEvent) {
    this.activeFieldPath = fieldPath;
    if (event) {
      this.activeFieldElement = event.target as HTMLInputElement | HTMLTextAreaElement;
      this.updateCursorPosition();
    }
  }
  
  /**
   * Inserta una variable en el campo que actualmente tiene focus
   */
  insertVariableInActiveField(variable: string) {
    // No permitir inserción en tab "Información Básica"
    if (!this.canInsertVariables || !this.activeFieldPath || !this.activeFieldElement) {
      return;
    }
    
    // Actualizar la posición del cursor antes de insertar
    this.updateCursorPosition();
    
    const currentValue = this.form.get(this.activeFieldPath)?.value || '';
    const variableText = `{{${variable}}}`;
    
    // Insertar en la posición del cursor
    const textBeforeCursor = currentValue.substring(0, this.cursorPosition);
    const textAfterCursor = currentValue.substring(this.cursorPosition);
    const newValue = textBeforeCursor + variableText + textAfterCursor;
    
    // Actualizar el valor del formulario
    this.form.get(this.activeFieldPath)?.setValue(newValue);
    
    // Calcular nueva posición del cursor (después de la variable insertada)
    const newCursorPosition = this.cursorPosition + variableText.length;
    
    // Restaurar foco y posición del cursor
    setTimeout(() => {
      if (this.activeFieldElement) {
        this.activeFieldElement.focus();
        this.activeFieldElement.setSelectionRange(newCursorPosition, newCursorPosition);
        this.cursorPosition = newCursorPosition;
      }
    }, 0);
    
    // Actualizar automáticamente la lista de variables usadas
    this.templateService.updateVariables();
  }
  
  /**
   * Obtiene el label amigable del campo activo
   */
  getFieldLabel(fieldPath: string): string {
    const labels: Record<string, string> = {
      'emailTemplate.subject': 'Asunto del Email',
      'emailTemplate.html': 'Contenido HTML',
      'smsTemplate.message': 'Mensaje SMS',
      'whatsappTemplate.message': 'Mensaje WhatsApp',
      'callScript.greeting': 'Saludo de Llamada',
      'callScript.mainMessage': 'Mensaje Principal',
      'callScript.closing': 'Despedida de Llamada',
      'autoCallConfig.ttsMessage': 'Mensaje TTS'
    };
    return labels[fieldPath] || 'Campo activo';
  }

  insertVariable(variable: string, field: string) {
    // Insertar variable en el campo usando la clave técnica
    const currentValue = this.form.get(field)?.value || '';
    this.form.get(field)?.setValue(currentValue + `{{${variable}}}`);
    
    // Actualizar automáticamente la lista de variables usadas
    this.templateService.updateVariables();
  }

  extractAndUpdateVariables() {
    // Usar el método del servicio que ya extrae de todos los campos
    this.templateService.updateVariables();
  }

  /**
   * Sanitiza automáticamente el mensaje SMS al escribir
   * Aplica sanitización GSM-7 y truncado a 160 caracteres en tiempo real
   */
  onSmsMessageInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const currentValue = textarea.value;
    
    // Sanitizar y truncar
    const sanitized = this.smsEncodingService.sanitizeAndTruncate(currentValue, 160);
    
    // Solo actualizar si cambió (evitar loops infinitos)
    if (currentValue !== sanitized) {
      // Actualizar FormControl
      this.smsTemplate.get('message')?.setValue(sanitized, { emitEvent: false });
      
      // Restaurar posición del cursor al final
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = sanitized.length;
      }, 0);
    }
    
    // Actualizar posición del cursor para inserción de variables
    this.updateCursorPosition();
  }

  // ============================================
  // MÉTODOS PARA MANEJO DE AUDIO
  // ============================================

  /**
   * Maneja la selección de archivo de audio
   */
  onAudioFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav'];
      if (!validTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Tipo no válido',
          detail: 'Solo se permiten archivos MP3 o WAV'
        });
        return;
      }
      
      // Validar tamaño (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          summary: 'Archivo muy grande',
          detail: 'El archivo debe ser menor a 10MB'
        });
        return;
      }
      
      this.selectedAudioFile = file;
      this.audioPreviewUrl = URL.createObjectURL(file);
      this.autoCallConfig.get('audioFileName')?.setValue(file.name);
    }
  }

  /**
   * Sube el audio a Azure y actualiza el formulario
   */
  uploadAudio() {
    if (!this.selectedAudioFile) return;
    
    this.isUploadingAudio = true;
    this.apiCallsService.uploadAudio(this.selectedAudioFile).subscribe({
      next: (response) => {
        this.isUploadingAudio = false;
        if (!response.error && response.data) {
          this.autoCallConfig.get('audioUrl')?.setValue(response.data.audioUrl);
          this.autoCallConfig.get('audioFileName')?.setValue(response.data.fileName);
          this.messageService.add({
            severity: 'success',
            summary: 'Audio subido',
            detail: 'El audio se ha subido correctamente'
          });
          // Limpiar el archivo seleccionado ya que ahora está en Azure
          this.selectedAudioFile = null;
          if (this.audioPreviewUrl) {
            URL.revokeObjectURL(this.audioPreviewUrl);
            this.audioPreviewUrl = null;
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error al subir',
            detail: response.msg || 'No se pudo subir el audio'
          });
        }
      },
      error: (error) => {
        this.isUploadingAudio = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error de conexión al subir el audio'
        });
      }
    });
  }

  /**
   * Limpia el audio seleccionado o subido
   */
  clearAudio() {
    this.selectedAudioFile = null;
    if (this.audioPreviewUrl) {
      URL.revokeObjectURL(this.audioPreviewUrl);
      this.audioPreviewUrl = null;
    }
    this.autoCallConfig.get('audioUrl')?.setValue('');
    this.autoCallConfig.get('audioFileName')?.setValue('');
  }

  /**
   * Limpia recursos al destruir el componente
   */
  ngOnDestroy() {
    if (this.audioPreviewUrl) {
      URL.revokeObjectURL(this.audioPreviewUrl);
    }
  }
}
